import React, { useState, useContext } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ArrowMenu from '../../../../../ArrowMenu';
import Checkbox from '../../../../../Form/Checkbox';

import getResizedImageUrl from '../../../../../../helpers/getResizedImageLogoUrl';
import { CompanyContext } from '../../../../../../context/CompanyContext';
import classes from './styles.module.scss';
import FavStatus from '../../../../Header/Search/FavStatus';
import CompanyAvatar from '../../CompanyAvatar';
import Tooltip from '../../../../../Tooltip';
import NotesIcon from '../../../../../../assets/images/common/shared/notes.svg';

export default function CompanyGridCard({
  reference,
  company,
  refetch,
  toggleCandidateSelection,
  selectedRows,
  showCompanyInfo,
  isFavorite = false,
}) {
  const [isArrowMenuVisible, setIsArrowMenuVisible] = useState(false);

  const { showAddCompanyNoteModal } = useContext(CompanyContext);
  const { t } = useTranslation();

  const navigate = useNavigate();

  const options = [
    {
      label: company?.note
        ? t(
            'dashboardComponents.Companies.CompaniesList.GridCard.options.editNote'
          )
        : t(
            'dashboardComponents.Companies.CompaniesList.GridCard.options.addNote'
          ),
      onClick: () => showAddCompanyNoteModal(company, refetch),
    },
    {
      label: t('common.postJob'),
      onClick: () =>
        navigate(`/tools/jobs/post-job?sourceCompanyId=${company.id}`),
    },
  ];

  const isJoined = company.jobApplications?.some?.(
    (jobApp) => jobApp.subStatus === 'Joined'
  );

  if (isJoined) {
    options.splice(0, 1);
  }

  return (
    <div className={classes.container} ref={reference}>
      <div className={classes.border} />
      <div className={classes.footer}>
        <div className={classes.content}>
          <div className={classes.item}>
            <span className={classes.itemLabel}>
              {t(
                'dashboardComponents.Companies.CompaniesList.GridCard.lastActivity'
              )}
            </span>
            <span className={classes.itemValue}>
              {company.lastActivity?.format('DD MMM, YYYY') ?? '-'}
            </span>
          </div>

          <div className={classes.item}>
            <span className={classes.itemLabel}>
              {t(
                'dashboardComponents.Companies.CompaniesList.GridCard.primaryContact'
              )}
            </span>
            <span className={classes.itemValue}>
              {company.company.primaryManager?.firstName}{' '}
              {company.company.primaryManager?.lastName ?? '-'}
            </span>
          </div>

          <div className={classes.item}>
            <span className={classes.itemLabel}>
              {t('dashboardComponents.Companies.CompaniesList.GridCard.status')}
            </span>
            <span
              className={classNames(classes.itemValue, {
                [classes.active]: company.company.status === 'Active',
                [classes.prospect]: company.company.status === 'Prospect',
                [classes.inactive]: company.company.status === 'Inactive',
              })}
            >
              {company.company.status ?? '-'}
            </span>
          </div>
        </div>
      </div>
      <div
        className={classNames(classes.CompanyGridCard)}
        onClick={() => {
          showCompanyInfo(company.id);
        }}
      >
        <header className={classes.header}>
          {!isFavorite ? (
            <Checkbox
              isSelected={selectedRows.includes(company.id)}
              onChange={(e) => {
                e.stopPropagation();
                toggleCandidateSelection(company.id);
              }}
            />
          ) : (
            <div />
          )}
          <div className={classes.actionsContainer}>
            <Tooltip text="Company ID" noWrap direction="left">
              <span className={classes.idLabel}>#{company.company.id}</span>
            </Tooltip>
            <div className={classes.notesContainer}>
              <img src={NotesIcon} alt="Notes" />
              <span>
                {company.company?.companyNotes?.length > 0
                  ? company.company?.companyNotes?.length
                  : 0}
              </span>
            </div>
            <FavStatus company={company} refetch={refetch} />
            <ArrowMenu
              options={options}
              isVisible={isArrowMenuVisible}
              setIsVisible={setIsArrowMenuVisible}
            >
              <div className={classes.threeDots} />
            </ArrowMenu>
          </div>
        </header>

        <div className={classes.infoContainer}>
          <div className={classes.headerContainer}>
            <div className={classes.companyInfo}>
              <div className={classes.companyLogoContainer}>
                {company.company.logo ? (
                  <div className={classes.companyLogo}>
                    <img
                      src={getResizedImageUrl({
                        url: `company/${company.company.logo}`,
                        width: 60,
                      })}
                      alt="Company"
                    />
                  </div>
                ) : (
                  <CompanyAvatar companyName={company.company?.name} />
                )}
              </div>

              <div className={classes.companyInfoGroup}>
                <h3>{company.company.name}</h3>
                <span className={classes.location}>
                  {company.company.industry
                    ? `${company.company.industry.name}, `
                    : ''}
                  {company.company.locations[0]?.addressCity
                    ? `${company.company.locations[0].addressCity}`
                    : ''}
                </span>
              </div>
            </div>
          </div>
          <div className={classes.countsContainer}>
            <div className={classes.count}>
              <span
                className={classNames(classes.countValue, [
                  classes.countValue,
                  (company.activeJobsCount === 0 ||
                    company.activeJobsCount === null ||
                    company.activeJobsCount === undefined) &&
                    classes.zero,
                ])}
              >
                {company.activeJobsCount ?? 0}
              </span>
              <span className={classes.countLabel}>
                {t(
                  'dashboardComponents.Companies.CompaniesList.GridCard.openJobs'
                )}
              </span>
            </div>
            <div className={classes.count}>
              <span
                className={classNames(classes.countValue, [
                  classes.countValue,
                  (company.activeCandidatesCount === 0 ||
                    company.activeCandidatesCount === null ||
                    company.activeCandidatesCount === undefined) &&
                    classes.zero,
                ])}
              >
                {company.activeCandidatesCount ?? 0}
              </span>
              <span className={classes.countLabel}>
                {t(
                  'dashboardComponents.Companies.CompaniesList.GridCard.activeCandidates'
                )}
              </span>
            </div>
            <div className={classes.count}>
              <span
                className={classNames(classes.countValue, [
                  classes.countValue,
                  (company.hiresCount === 0 ||
                    company.hiresCount === null ||
                    company.hiresCount === undefined) &&
                    classes.zero,
                ])}
              >
                {company.hiresCount ?? 0}
              </span>
              <span className={classes.countLabel}>
                {t(
                  'dashboardComponents.Companies.CompaniesList.GridCard.hires'
                )}
              </span>
            </div>
            <Tooltip text="Avg. Response (days)" noWrap direction="left">
              <div className={classes.count}>
                <span
                  className={classNames(classes.countValue, [
                    (company.sla === 0 ||
                      company.sla === null ||
                      company.sla === undefined) &&
                      classes.zero,
                  ])}
                >
                  {company.sla ?? 0}
                </span>
                <span className={classes.countLabel}>
                  {t(
                    'dashboardComponents.Companies.CompaniesList.GridCard.slaResp'
                  )}
                </span>
              </div>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
}
